<script lang="ts">
	import { onMount } from 'svelte';
	import { FormComponents } from './FormComponents';
	import { Config } from '#lib/constants/config.js';
	import { PrivacyPolicyDisclaimer } from '#lib/features/checkout/components/ConfirmStep/PrivacyPolicyDisclaimer/index.js';
	import { NavBar } from '#lib/features/navigation/components/NavBar/index.js';
	import { NavBarLogo } from '#lib/features/navigation/components/NavBarLogo/index.js';
	import { PageBody } from '#lib/features/navigation/components/PageBody/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { createNewsletterRecaptchaMutation } from '#lib/features/newsletter/utils/createNewsletterRecaptchaMutation/createNewsletterRecaptchaMutation.js';
	import { Recaptcha } from '#lib/features/tag-managers/components/Recaptcha/index.js';
	import { getTimeContext } from '#lib/features/time/utils/getTimeContext/getTimeContext.js';
	import { createTurnstile } from '#lib/features/turnstile/utils/createTurnstile/createTurnstile.svelte.js';
	import { isOnProductionOnly } from '#lib/utils/env/isOnProductionOnly/isOnProductionOnly.js';
	import { toggleRecaptchaBadge } from '#lib/utils/toggleRecaptchaBadge/toggleRecaptchaBadge.js';
	import { page } from '$app/state';
	import { ErrorIcon } from '$global/assets/styled-icons/ErrorIcon';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { Button } from '$global/components/Button';
	import { Checkbox } from '$global/components/Checkbox';
	import { InputLabel } from '$global/components/InputLabel';
	import { LinkButton } from '$global/components/LinkButton';
	import { showToast, Toasts } from '$global/components/Toasts';
	import { Turnstile } from '$global/components/Turnstile';
	import { Txt } from '$global/components/Txt';
	import { fetchWithRetry } from '$global/utils/fetchWithRetry/fetchWithRetry';
	import { getDirectusDateTimestamp } from '$global/utils/getDirectusDateTimestamp';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import { getRecaptchaToken } from '$global/utils/getRecaptchaToken/getRecaptchaToken';
	import { identifyUser } from '$global/utils/identifyUser';
	import { isCypress } from '$global/utils/isCypress/isCypress';
	import { scrollToTop } from '$global/utils/scrollToTop';
	import { track } from '$global/utils/track';
	import {
		AuthAction,
		AuthMethod,
		AuthProvenance,
		trackAuthEvent,
	} from '$global/utils/trackAuthEvent';
	import { validateEmail } from '$global/utils/validateEmail/validateEmail';
	import type {
		Form_Page_Form_Components,
		Form_Group_Form_Group_Components,
	} from '$gql/types';

	import type { FormsPageData } from '$routes/forms/[slug]/types';

	type FormValue = string | number | boolean | File | File[] | null;

	const dataCy = 'form-page';

	const { fetchTime } = getTimeContext();

	onMount(() => {
		// Check for email query parameter and prefill if present
		const emailParam = page.url.searchParams.get('email');
		if (emailParam && formPage?.form_components) {
			const emailFieldNames = collectEmailFields(
				formPage.form_components as (
					Form_Page_Form_Components | Form_Group_Form_Group_Components | null
				)[]
			);
			// Prefill email fields
			emailFieldNames.forEach((fieldName) => {
				formValues[fieldName] = emailParam;
				// Trigger blur state to show validation if email is invalid
				formBlurs[fieldName] = true;
			});
		}

		fetchTime().then((currentTime: number | undefined) => {
			if (!currentTime) {
				isExpired = false;
				return;
			}

			isExpired =
				currentTime >=
				// `available_until` is nullable REST-side (GraphQL's `any` scalar
				// masked this previously); cast preserves the pre-existing runtime
				// behaviour of passing a possibly-null value straight through.
				getDirectusDateTimestamp(data?.formPage?.available_until as string);
		});

		return toggleRecaptchaBadge();
	});

	let isExpired = $state(null as null | boolean);
	let data = $derived(getPageData<FormsPageData>(page.data));
	let formPage = $derived(data?.formPage);
	let success = $state(false);
	let formValues = $state({} as Record<string, FormValue>);
	let formBlurs = $state({} as Record<string, boolean>);

	let requiredFields: string[] = [];
	let emailFields: string[] = [];
	let isFormComplete = $state(false);
	let isSubmitting = $state(false);
	let newsletterOptIn = $state(false);

	// Turnstile runs in the browser only; bypass the gate under Cypress e2e runs.
	const turnstile = createTurnstile();

	const subscribeToNewsletterRecaptcha = createNewsletterRecaptchaMutation();

	let newsletterListId = $derived(
		isOnProductionOnly()
			? formPage?.newsletter_list_id
			: formPage?.test_newsletter_list_id
	);

	let redirectUrl = $derived(formPage?.redirect_url || GlobalRoutes.Home);

	const isFieldComponent = (
		item: unknown
	): item is { field_name: string; required: boolean } => {
		return (
			!!item &&
			typeof (item as { field_name?: unknown }).field_name === 'string' &&
			(item as { required?: unknown }).required === true
		);
	};

	const collectRequiredFields = (
		components:
			| (Form_Page_Form_Components | Form_Group_Form_Group_Components | null)[]
			| null
			| undefined
	): string[] => {
		const fields: string[] = [];
		(components ?? []).forEach((component) => {
			if (!component) return;
			if (
				component.collection === 'Form_Group' &&
				component.item &&
				'form_group_components' in component.item &&
				component.item.form_group_components
			) {
				fields.push(
					...collectRequiredFields(component.item.form_group_components)
				);
			} else if (
				component.item &&
				isFieldComponent(component.item) &&
				!['Form_Video', 'Form_Image', 'Form_Text'].includes(
					component.collection || ''
				)
			) {
				fields.push(component.item.field_name);
			}
		});
		return fields;
	};

	const collectEmailFields = (
		components:
			| (Form_Page_Form_Components | Form_Group_Form_Group_Components | null)[]
			| null
			| undefined
	): string[] => {
		const fields: string[] = [];
		(components ?? []).forEach((component) => {
			if (!component) return;
			if (
				component.collection === 'Form_Group' &&
				component.item &&
				'form_group_components' in component.item &&
				component.item.form_group_components
			) {
				fields.push(
					...collectEmailFields(component.item.form_group_components)
				);
			} else if (
				component.collection === 'Form_Input' &&
				component.item &&
				'field_name' in component.item &&
				typeof component.item.field_name === 'string' &&
				component.item.field_name.includes('email')
			) {
				fields.push(component.item.field_name);
			}
		});
		return fields;
	};

	$effect(() => {
		if (!formPage?.form_components) {
			requiredFields = [];
			emailFields = [];
			return;
		}
		requiredFields = collectRequiredFields(
			formPage.form_components as (
				Form_Page_Form_Components | Form_Group_Form_Group_Components | null
			)[]
		);
		emailFields = collectEmailFields(
			formPage.form_components as (
				Form_Page_Form_Components | Form_Group_Form_Group_Components | null
			)[]
		);
	});

	$effect(() => {
		if (requiredFields.length === 0) {
			isFormComplete = true;
			return;
		}

		const missing = requiredFields.filter((field) => {
			const value = formValues[field];
			if (typeof value === 'boolean') return false;
			if (typeof value === 'number') return false;
			if (value instanceof File) return false;
			if (Array.isArray(value) && value.length > 0 && value[0] instanceof File)
				return false;
			return !value || value === '';
		});

		const invalidEmails = emailFields.filter((field) => {
			const value = formValues[field];
			if (!value || typeof value !== 'string') return false;
			return !validateEmail(value);
		});

		isFormComplete = missing.length === 0 && invalidEmails.length === 0;
	});

	const updateFormValue = (fieldPath: string, value: FormValue) => {
		formValues = {
			...formValues,
			[fieldPath]: value,
		};
	};

	const findDiscordIdInputId = (
		components: (
			Form_Page_Form_Components | Form_Group_Form_Group_Components | null
		)[]
	): string | undefined =>
		components.reduce<string | undefined>((found, comp) => {
			if (found) return found;
			if (!comp) return undefined;
			if (
				comp.collection === 'Form_Group' &&
				comp.item &&
				'form_group_components' in comp.item &&
				comp.item.form_group_components
			) {
				return findDiscordIdInputId(comp.item.form_group_components) || found;
			}
			if (
				comp.collection === 'Form_Input' &&
				comp.item &&
				'field_name' in comp.item &&
				comp.item.field_name === 'discord_id'
			) {
				return comp.item.id;
			}
			return found;
		}, undefined);

	const handleSubmit = async (e: Event) => {
		e.preventDefault();

		if (!isFormComplete || !turnstile.isTurnstileVerified) {
			return;
		}

		isSubmitting = true;
		const formData = new FormData();

		if (turnstile.turnstileToken) {
			formData.append('turnstile_token', turnstile.turnstileToken);
		}

		try {
			const recaptchaToken = await getRecaptchaToken(Config.RecaptchaKey);
			if (recaptchaToken) {
				formData.append('recaptcha_token', recaptchaToken);
			}
			// eslint-disable-next-line no-empty
		} catch {}

		try {
			const discordIdInputId = findDiscordIdInputId(
				(formPage.form_components || []) as Parameters<
					typeof findDiscordIdInputId
				>[0]
			);

			Object.entries(formValues).forEach(([key, value]) => {
				if (key === 'discord_id' && discordIdInputId) {
					formData.append('discord_id', discordIdInputId);
				} else if (value instanceof File) {
					formData.append('file', value);
				} else if (Array.isArray(value) && value[0] instanceof File) {
					value.forEach((file) => formData.append(key, file));
				} else if (value != null) {
					formData.append(key, String(value));
				}
			});

			track('FORMS/SUBMIT');

			const formDataEmail = formData.get('email') as string;
			const formDataFirstName = formData.get('first_name') as string;
			const formDataLastName = formData.get('lastname') as string;

			if (formDataEmail && formDataFirstName && formDataLastName) {
				await identifyUser({
					firstName: formDataFirstName,
					lastName: formDataLastName,
					email: formDataEmail,
				});
				trackAuthEvent({
					action: AuthAction.Identified,
					method: AuthMethod.Email,
					provenance: AuthProvenance.Forms,
				});
			}

			if (isOnProductionOnly() && window?.fbq) {
				window?.fbq('track', 'Lead');
			}

			const response = await fetchWithRetry(
				`/api/submit-form?formId=${formPage.id}`,
				{
					method: 'POST',
					body: formData,
				}
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.message || `HTTP ${response.status}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error('Form submission failed');
			} else {
				if (newsletterOptIn && newsletterListId) {
					try {
						const newsletterRecaptchaToken = await getRecaptchaToken(
							Config.RecaptchaKey
						);
						const email = formData.get('email') as string;
						if (email && newsletterRecaptchaToken) {
							await subscribeToNewsletterRecaptcha.mutateAsync({
								email,
								listId: newsletterListId,
								recaptcha_token: newsletterRecaptchaToken,
								...(formData.get('first_name') && {
									first_name: formData.get('first_name') as string,
								}),
								...(formData.get('lastname') && {
									last_name: formData.get('lastname') as string,
								}),
							});
						}
					} catch {
						// Newsletter subscription failure should not block form success
					}
				}

				scrollToTop();
				success = true;
			}
		} catch (error) {
			showToast({
				variant: 'error',
				message:
					error instanceof Error
						? error.message
						: 'Failed to submit form. Please try again..',
			});

			// Turnstile tokens are single-use; reset so the user can resubmit.
			turnstile.reset();
			isSubmitting = false;
		}
	};

	let crumbs = $derived([
		{ label: 'Home', href: GlobalRoutes.Home },
		{ label: `${data.formPage.seo?.title}` },
	]);
</script>

<NavBar dark>
	{#snippet homeIcon()}
		<NavBarLogo />
	{/snippet}
</NavBar>

<Recaptcha />

<PageBody
	class="mb-0 min-h-[max(calc(100vh-448px),480px)] bg-gray-100 pb-10 lg:mb-0 lg:pb-[4rem]"
>
	<Breadcrumbs {dataCy} {crumbs} class="mt-0 pt-4 lg:mt-0 lg:pt-6" />
	{#if isExpired !== null}
		<div class="px-4 sm:px-6">
			{#if success}
				<div
					class="mx-auto mt-10 flex max-w-[32.5rem] flex-col items-center justify-center rounded bg-white py-10 text-center shadow"
				>
					{#each formPage?.success_page_components || [] as component, i}
						<FormComponents
							index={i}
							component={component as Form_Page_Form_Components}
							{dataCy}
							{formValues}
							{formBlurs}
							{updateFormValue}
						/>
					{/each}
					<div class="flex w-full justify-center px-4 md:max-w-[70%]">
						<LinkButton
							dataCy={`${dataCy}-done`}
							variant="primary"
							size="md"
							class="w-full [&>button]:w-full"
							href={redirectUrl}
						>
							Done
						</LinkButton>
					</div>
				</div>
			{:else if isExpired}
				<div
					class="mx-auto mt-10 flex max-w-[32.5rem] flex-col items-center justify-center rounded bg-white py-10 text-center shadow"
				>
					<div class="mb-4 flex justify-center">
						<ErrorIcon class="h-[5rem] w-[5rem]" />
					</div>
					<Txt variant="h2" class="mb-4">Form closed</Txt>
					<Txt variant="body2" class="mb-4"
						>This form is no longer available.</Txt
					>
					<LinkButton
						{dataCy}
						href={redirectUrl}
						variant="primary"
						size="lg"
						fullWidth
						class="mt-3"
					>
						Continue to HENI
					</LinkButton>
				</div>
			{:else}
				<div
					class="mx-auto mt-10 flex max-w-[32.5rem] flex-col items-center justify-center rounded bg-white text-center shadow"
				>
					<form onsubmit={handleSubmit}>
						{#each formPage?.form_components || [] as component, i}
							<FormComponents
								index={i}
								component={component as Form_Page_Form_Components}
								{dataCy}
								{formValues}
								{formBlurs}
								{updateFormValue}
							/>
						{/each}
						{#if newsletterListId}
							<InputLabel
								dataCy={`${dataCy}-newsletter`}
								class="px-4"
								classes={{ wrapper: 'items-start' }}
								variant="body3"
							>
								<Checkbox
									dataCy={`${dataCy}-newsletter-label`}
									name="newsletter"
									class="mt-[-0.125rem]"
									bind:checked={newsletterOptIn}
								/>

								<div class="-mt-1 pl-1.5 text-left">
									I would like to sign up for the HENI Newsletter to receive
									updates on future drops, products and events.
								</div>
							</InputLabel>
						{/if}
						{#if !isCypress()}
							<div class="mt-4 flex flex-col items-center px-4">
								<Turnstile
									dataCy={`${dataCy}-turnstile`}
									publicTurnstileKey={Config.TurnstileKey}
									bind:reset={turnstile.resetTurnstile}
									onVerify={turnstile.handleTurnstileVerify}
									onExpired={turnstile.handleTurnstileFailure}
									onError={turnstile.handleTurnstileFailure}
									hasError={turnstile.turnstileError}
								/>
							</div>
						{/if}
						<div class="mt-4 flex justify-center px-4">
							{#key isFormComplete || turnstile.isTurnstileVerified}
								<Button
									dataCy={`${dataCy}-submit-button`}
									variant="primary"
									size="md"
									type="submit"
									class="w-full md:max-w-[70%]"
									disabled={!isFormComplete || !turnstile.isTurnstileVerified}
									loading={isSubmitting}
								>
									{formPage?.submit_button_text}
								</Button>
							{/key}
						</div>
						<PrivacyPolicyDisclaimer
							href={GlobalRoutes.PrivacyUk}
							class="mb-4 px-4 leading-[1rem]"
						/>
					</form>
				</div>
			{/if}
		</div>
	{/if}
</PageBody>

<Toasts />
